// Lookup helpers for the static brand-crawl content. Used as a fallback
// when Sanity has no matching page/post yet, keeps every migrated URL
// from idukki.io rendering with real text + images even before the
// WordPress→Sanity migration is wired up end-to-end.
import { brand, type PageOutline, type Asset } from './brand'
import { reservedTopLevelSlugs } from './static-routes'
import { unsplashPlaceholderFor, guessCategoryFromText, type PlaceholderCategory } from './placeholder-images'

function normalize(slug: string): string {
  return slug.replace(/^\/+|\/+$/g, '')
}

export function findBrandPage(slug: string): PageOutline | null {
  const target = normalize(slug)
  if (!target) return null
  for (const p of brand.pages) {
    const candidate = normalize(p.slug)
    if (candidate === target) return p
  }
  return null
}

export function findBrandBlogPost(slug: string): PageOutline | null {
  const target = normalize(slug)
  if (!target) return null
  // The crawler emits blog slugs as `blog/<slug>`. Match on either.
  return findBrandPage(`blog/${target}`) || findBrandPage(target.startsWith('blog/') ? target : `blog/${target}`)
}

// Top-level slugs (no `/` in path) we have *any* metadata for, even if
// the body hasn't been crawled yet. Reserved app routes are excluded.
export function listBrandTopLevelPageSlugs(): string[] {
  return brand.pages
    .map((p) => normalize(p.slug))
    .filter((s) => s && !s.includes('/') && s !== 'home' && !reservedTopLevelSlugs.includes(s))
}

// Blog slugs we have *any* metadata for. We render every one of these
// because even a stub page (title + meta + "open original" link) is
// better than a 404 while the full body crawl catches up.
export function listBrandBlogSlugs(): string[] {
  const out = new Set<string>()
  for (const p of brand.pages) {
    const s = normalize(p.slug)
    if (!s.startsWith('blog/')) continue
    out.add(s.slice('blog/'.length))
  }
  return Array.from(out)
}

export function listBrandBlogPosts(): PageOutline[] {
  return brand.pages.filter((p) => normalize(p.slug).startsWith('blog/'))
}

// Imported /case-studies/* pages from idukki.io. The marketing site's
// /case-studies index folds these in alongside the hand-curated
// registry; the [slug] route falls back to rendering the imported
// body when the slug isn't in the registry.
// Imported /products/* pages from idukki.io. The /products index
// folds these in; /products/[slug] renders them with the WpHtml
// component (sanitized HTML body + cached images).
export function listBrandProducts(): PageOutline[] {
  return brand.pages.filter((p) => {
    const s = normalize(p.slug)
    return s.startsWith('products/') || s.startsWith('product/')
  })
}

export function findBrandProduct(slug: string): PageOutline | null {
  const target = normalize(slug)
  if (!target) return null
  for (const p of brand.pages) {
    const s = normalize(p.slug)
    for (const prefix of ['products/', 'product/']) {
      if (s === `${prefix}${target}`) return p
    }
  }
  return null
}

export function listBrandCaseStudies(): PageOutline[] {
  return brand.pages.filter((p) => {
    const s = normalize(p.slug)
    return s.startsWith('case-studies/') || s.startsWith('case-study/') || s.startsWith('customer-stories/') || s.startsWith('customers/')
  })
}

export function findBrandCaseStudy(slug: string): PageOutline | null {
  const target = normalize(slug)
  if (!target) return null
  for (const p of brand.pages) {
    const s = normalize(p.slug)
    for (const prefix of ['case-studies/', 'case-study/', 'customer-stories/', 'customers/']) {
      if (s === `${prefix}${target}` || s === target) return p
    }
  }
  return null
}

// Formats every mainstream link-unfurler (WhatsApp, Slack, iMessage,
// Facebook) reliably renders as an og:image. SVG in particular is
// silently dropped by all of them (no image shown, not even an error),
// and AVIF/GIF support is inconsistent — so any cover picked here that
// ends up as a page's og:image must be one of these or the shared link
// unfurls with no preview image at all.
const OG_SAFE_EXT = /\.(jpe?g|png|webp)$/i
function isOgSafe(asset: Asset | null | undefined): asset is Asset {
  return !!asset && OG_SAFE_EXT.test(asset.ref)
}

// Pick a representative cover for a page. In order: per-page crawled
// cover (rejected if it looks like an avatar/Gravatar, those slipped
// through the crawler's first-image heuristic for some posts, or if
// it's a format link-unfurlers can't render), an evocative screenshot
// from the global manifest (rotated by slug hash so different pages
// get different visuals), the site hero, the OG image. Returns null if
// nothing is available.
export function pickCoverFor(page: PageOutline): Asset | null {
  if (page.cover && !looksLikeAvatar(page.cover) && isOgSafe(page.cover)) return page.cover
  return rotatedScreenshotFor(page.slug) || (isOgSafe(page.cover) ? page.cover : null) || brand.assets?.hero || brand.assets?.og || null
}

// Same lookup, but always rotate from the global screenshot pool
// (skipping per-page covers entirely). Used by surfaces where we
// can't tolerate even a small chance of an author headshot leaking
// in, the mega-menu archive strip, blog "From the archive" cards
// and the /from-idukki index. Substantive and consistent visuals
// matter more than per-post accuracy on those surfaces.
export function rotatedCoverFor(page: PageOutline): Asset | null {
  return rotatedScreenshotFor(page.slug)
    || (page.cover && !looksLikeAvatar(page.cover) && isOgSafe(page.cover) ? page.cover : null)
    || brand.assets?.hero
    || brand.assets?.og
    || null
}

function rotatedScreenshotFor(slug: string): Asset | null {
  const screenshots = (brand.screenshots || []).filter(isOgSafe)
  if (!screenshots.length) return null
  return screenshots[simpleHash(slug) % screenshots.length] || null
}

// Returns a Unsplash-backed placeholder image URL for any slug. Used
// as a final fallback when the crawler has nothing usable. Optional
// category constrains the photo pool; if omitted we guess from the
// slug text.
export function placeholderImageFor(
  slug: string,
  category?: PlaceholderCategory,
  width = 1200,
  height = 800,
): string {
  return unsplashPlaceholderFor(slug, {
    category: category || guessCategoryFromText(slug),
    width,
    height,
  })
}

// One-stop helper used by every card surface: returns a guaranteed
// image URL, first the per-page cover (if substantive), else a
// rotated screenshot, else a curated Unsplash photo. Never returns
// null, so cards never have to render a flat gradient again.
export function coverOrPlaceholderFor(
  page: PageOutline,
  category?: PlaceholderCategory,
): string {
  const cover = pickCoverFor(page)
  if (cover) return cover.ref
  return placeholderImageFor(page.slug || page.title, category)
}

// Heuristic: cover images that are tiny (<20KB), gravatar-hosted,
// have "avatar"/"author"/"profile" in the source URL, or are clearly
// thumbnail-sized are almost always bylines, not real covers.
function looksLikeAvatar(asset: Asset): boolean {
  const url = (asset.sourceUrl || '').toLowerCase()
  const ref = (asset.ref || '').toLowerCase()
  if (asset.bytes && asset.bytes < 20000) return true
  if (/gravatar\.com|secure\.gravatar/.test(url)) return true
  if (/(avatar|author|byline|profile|headshot|user-?icon|wp-user-avatar)/.test(url + ' ' + ref)) return true
  // WordPress thumbnail size markers: -150x150, -96x96 etc.
  if (/-(\d{1,3})x\1\.(jpe?g|png|webp|gif)/.test(url)) return true
  return false
}

function simpleHash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h
}
